<template>
    <div class="psy-paper" v-if="paper" :data-form="formType">
        <header class="psy-paper__head">
            <h2 class="psy-paper__title">{{ displayTitle }}</h2>
        </header>

        <section
            v-for="(section, si) in sections"
            :key="'s-' + si"
            class="psy-paper__section"
        >
            <h3 v-if="sectionTitle(section)" class="psy-paper__section-title">
                {{ sectionTitle(section) }}
            </h3>

            <table class="psy-form-table">
                <colgroup>
                    <col v-for="n in 8" :key="'cg-' + n" />
                </colgroup>
                <tbody>
                    <tr v-for="(row, ri) in sectionRows(section)" :key="'r-' + ri">
                        <template v-for="node in flattenRow(row, section)">
                            <th
                                v-if="node.type === 'label'"
                                :key="'L-' + ri + '-' + node.key"
                                class="psy-form-table__label"
                            >{{ node.text }}</th>
                            <td
                                v-else
                                :key="'V-' + ri + '-' + node.key"
                                class="psy-form-table__value"
                                :class="{
                                    'is-empty': node.empty,
                                    'is-advice': node.advice,
                                    'is-full': node.full
                                }"
                                :colspan="node.colspan"
                            >{{ node.text || '—' }}</td>
                        </template>
                    </tr>
                </tbody>
            </table>
        </section>

        <section
            v-for="(repeater, pi) in repeaters"
            :key="'p-' + pi"
            class="psy-paper__section"
        >
            <h3 class="psy-paper__section-title">{{ repeaterTitle(repeater) }}</h3>
            <div class="psy-paper__scroll">
                <table class="psy-data-table">
                    <thead>
                        <tr>
                            <th
                                v-for="(col, ci) in repeaterColumns(repeater)"
                                :key="'h-' + ci"
                            >{{ col }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, ri) in repeaterRows(repeater)"
                            :key="'pr-' + ri"
                        >
                            <td
                                v-for="(col, ci) in repeaterColumns(repeater)"
                                :key="'pv-' + ci"
                                :class="{ 'is-empty': !rowCell(row, ci) }"
                            >{{ rowCell(row, ci) || '—' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <footer class="psy-paper__foot">
            <div class="psy-paper__sign">
                <span class="psy-paper__sign-label">填写人签名</span>
                <div class="psy-paper__sign-box">
                    <oss-el-image
                        v-if="signatureUrl"
                        :src="signatureUrl"
                        :school-id="schoolId"
                        fit="contain"
                        class="psy-paper__sign-img"
                        :preview-src-list="[signatureUrl]"
                    />
                    <span v-else class="psy-paper__sign-empty">未签名</span>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
import OssElImage from '../OssElImage.vue';

/**
 * 心理档案纸表预览：传统表格版式（标签+值并排，避免竖排挤字）。
 * 签名图走 OssElImage 预签名，兼容私有 Bucket。最后修改：2026-09-20
 * PC 源：SchoolPlatformWeb/src/components/Psychology/PsychologyPaperPreview.vue
 * 对接拷贝：docs/心理模块/学生心理档案重构/h5-assets/PsychologyPaperPreview.vue
 */
export default {
    name: 'PsychologyPaperPreview',
    components: { OssElImage },
    props: {
        paper: { type: Object, default: null },
        /** 学校 Id，用于私有 OSS 签名图预签名 */
        schoolId: { type: [String, Number], default: '' }
    },
    computed: {
        formType() {
            return String((this.paper && (this.paper.FormType || this.paper.formType)) || '').toLowerCase();
        },
        displayTitle() {
            return (this.paper && (this.paper.Title || this.paper.title)) || '';
        },
        sections() {
            return (this.paper && (this.paper.Sections || this.paper.sections)) || [];
        },
        repeaters() {
            return (this.paper && (this.paper.Repeaters || this.paper.repeaters)) || [];
        },
        signatureUrl() {
            return (this.paper && (this.paper.SignatureUrl || this.paper.signatureUrl)) || '';
        }
    },
    methods: {
        sectionTitle(section) {
            return (section && (section.Title || section.title)) || '';
        },
        sectionRows(section) {
            return (section && (section.Rows || section.rows)) || [];
        },
        rowCells(row) {
            return (row && (row.Cells || row.cells)) || [];
        },
        cellLabel(cell) {
            return (cell && (cell.Label || cell.label)) || '';
        },
        cellValue(cell) {
            const v = cell && (cell.Value != null ? cell.Value : cell.value);
            if (v == null) return '';
            return String(v).trim();
        },
        cellColspan(cell) {
            return Math.max(1, Number((cell && (cell.Colspan || cell.colspan)) || 1));
        },
        showCellLabel(cell, section) {
            const label = this.cellLabel(cell);
            const title = this.sectionTitle(section);
            return !!label && label !== title;
        },

        /** 展开为 th/td 节点序列，保证表格列对齐。创建于 2026-09-19 */
        flattenRow(row, section) {
            const cells = this.rowCells(row);
            const nodes = [];
            cells.forEach((cell, ci) => {
                const showLabel = this.showCellLabel(cell, section);
                if (showLabel) {
                    nodes.push({ type: 'label', key: 'l' + ci, text: this.cellLabel(cell) });
                }
                const val = this.cellValue(cell);
                nodes.push({
                    type: 'value',
                    key: 'v' + ci,
                    text: val,
                    empty: !val,
                    advice: this.isAdviceLabel(cell),
                    full: !showLabel,
                    colspan: this.valueColspan(cell, row, section, ci)
                });
            });
            return nodes;
        },

        /**
         * 固定 8 列网格：有标签时 label 占 1 列，剩余列按权重分给各 value。
         */
        valueColspan(cell, row, section, cellIndex) {
            const cells = this.rowCells(row);
            if (!cells.length) return 8;

            const labelSlots = cells.filter(c => this.showCellLabel(c, section)).length;
            const remain = Math.max(1, 8 - labelSlots);
            const weights = cells.map(c => this.cellColspan(c));
            const totalW = weights.reduce((a, b) => a + b, 0) || cells.length;
            const idx = cellIndex == null ? 0 : cellIndex;
            const w = weights[idx] || 1;

            // 最后一项吃掉舍入误差，保证一行总列数 = 8
            if (idx === cells.length - 1) {
                let used = 0;
                for (let i = 0; i < cells.length - 1; i++) {
                    used += Math.max(1, Math.round((remain * weights[i]) / totalW));
                }
                return Math.max(1, remain - used);
            }
            return Math.max(1, Math.round((remain * w) / totalW));
        },
        isAdviceLabel(cell) {
            const label = this.cellLabel(cell);
            return /处理建议|指引|症状|诊断|原因|方案/.test(label);
        },
        repeaterTitle(repeater) {
            return (repeater && (repeater.Title || repeater.title)) || '';
        },
        repeaterColumns(repeater) {
            return (repeater && (repeater.Columns || repeater.columns)) || [];
        },
        repeaterRows(repeater) {
            return (repeater && (repeater.Rows || repeater.rows)) || [];
        },
        rowCell(row, ci) {
            if (!row) return '';
            const v = Array.isArray(row) ? row[ci] : (row[ci] != null ? row[ci] : '');
            return v == null ? '' : String(v).trim();
        }
    }
};
</script>

<style scoped>
.psy-paper {
    --ink: #111827;
    --muted: #6b7280;
    --line: #9ca3af;
    --label-bg: #f3f4f6;
    --paper: #ffffff;
    --accent: #1f4e79;
    max-width: 820px;
    margin: 0 auto;
    padding: 28px 32px 24px;
    background: var(--paper);
    border: 1px solid #d1d5db;
    color: var(--ink);
    font-family: "Source Han Sans SC", "Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif;
}

.psy-paper__head {
    text-align: center;
    margin-bottom: 20px;
    padding-bottom: 14px;
    border-bottom: 2px solid var(--ink);
}

.psy-paper__title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.12em;
    line-height: 1.4;
    color: var(--ink);
}

.psy-paper__section {
    margin-bottom: 16px;
}

.psy-paper__section-title {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 700;
    color: var(--accent);
    letter-spacing: 0.06em;
}

.psy-form-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid var(--line);
    font-size: 13px;
    background: #fff;
}

.psy-form-table__label {
    width: 88px;
    max-width: 96px;
    padding: 9px 8px;
    background: var(--label-bg);
    border: 1px solid var(--line);
    color: #374151;
    font-weight: 600;
    font-size: 12px;
    text-align: center;
    white-space: nowrap;
    vertical-align: middle;
    line-height: 1.4;
}

.psy-form-table__value {
    padding: 9px 12px;
    border: 1px solid var(--line);
    color: var(--ink);
    vertical-align: middle;
    line-height: 1.65;
    word-break: break-word;
    white-space: pre-wrap;
    background: #fff;
}

.psy-form-table__value.is-empty {
    color: #d1d5db;
}

.psy-form-table__value.is-full {
    background: #fafafa;
}

.psy-form-table__value.is-advice {
    min-height: 64px;
    vertical-align: top;
    background: #fafafa;
}

.psy-paper[data-form="homeroom"] .psy-form-table__value.is-advice {
    background: #fffbeb;
    color: #78350f;
}

.psy-paper[data-form="psych"] .psy-paper__section-title {
    color: #1e3a8a;
}

.psy-paper[data-form="homeroom"] .psy-paper__section-title {
    color: #9a3412;
}

.psy-paper__scroll {
    overflow-x: auto;
    border: 1px solid var(--line);
}

.psy-data-table {
    width: 100%;
    min-width: 480px;
    border-collapse: collapse;
    font-size: 12px;
}

.psy-data-table th {
    padding: 8px 10px;
    background: var(--label-bg);
    border: 1px solid var(--line);
    color: #374151;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
}

.psy-data-table td {
    padding: 8px 10px;
    border: 1px solid var(--line);
    color: var(--ink);
    vertical-align: top;
    line-height: 1.5;
    word-break: break-word;
}

.psy-data-table td.is-empty {
    color: #d1d5db;
    text-align: center;
}

.psy-paper__foot {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 1px solid #d1d5db;
}

.psy-paper__sign {
    display: flex;
    align-items: flex-end;
    gap: 12px;
}

.psy-paper__sign-label {
    font-size: 12px;
    color: var(--muted);
    padding-bottom: 6px;
    white-space: nowrap;
}

.psy-paper__sign-box {
    width: 168px;
    height: 64px;
    border: 1px solid var(--line);
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.psy-paper__sign-img {
    width: 100%;
    height: 100%;
}

.psy-paper__sign-img >>> .el-image__inner {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.psy-paper__sign-img >>> .el-image__error,
.psy-paper__sign-img >>> .el-image__placeholder {
    font-size: 12px;
    color: #9ca3af;
}

.psy-paper__sign-empty {
    font-size: 12px;
    color: #d1d5db;
}

@media (max-width: 720px) {
    .psy-paper {
        padding: 18px 12px 16px;
    }

    .psy-paper__title {
        font-size: 16px;
        letter-spacing: 0.06em;
    }

    .psy-form-table__label {
        width: 72px;
        padding: 8px 4px;
        font-size: 11px;
    }

    .psy-form-table__value {
        padding: 8px 8px;
        font-size: 12px;
    }
}
</style>
